<template>
  <div class="profile-comments">
    <div class="fixed-section sticky top-[61px] w-full bg-gray-50 z-20">
      <!-- Nav 選單 -->
      <div class="border-b border-gray-500">
        <a 
          v-for="link in commentLinks" 
          :key="link.key"
          :href="link.path"
          :class="getNavLinkClass(link.key)"
          class="border-b-4 tab inline-block"
        >
          <div class="flex items-center py-3.5">
            {{ $t(link.titleKey) }}
          </div>
        </a>
      </div>
      
      <!-- 搜索表单 -->
      <div class="my-4">
        <search-input
          v-model="state.currentSearch"
          :placeholder="$t('models.model_search_form.search_model')"
          @search="handleSearch"
        />
      </div>

      <!-- 星級篩選 -->
      <div class="mb-4">
        <div class="reviews-status flex p-1">
          <button 
            v-for="rating in ratingOptions"
            :key="rating.value"
            @click="handleRatingFilter(rating.value)"
            :class="getRatingTabClass(rating.value)"
            class="text-center mr-1 py-1 pl-4 pr-1 rounded-full transition-colors"
          >
            <span class="flex items-center justify-center w-auto">
              {{ rating.label }}
              <span v-if="ratingCounts[rating.value] !== undefined" class="inline-block w-8 h-6 leading-6 bg-gray-500 rounded-full ml-2 text-14px opacity-75">
                {{ ratingCounts[rating.value] }}
              </span>
            </span>
          </button>
        </div>
      </div>
  
      <!-- 列表表頭 -->      
      <div class="w-full text-12px flex flex-wrap gap-x-4 mt-4 sm:grid sm:gap-x-8 font-[600]
                sm:grid-cols-[72px_37fr_130fr_36px] 
                ">
        <div class="ml-2 text-gray-200">
          {{ $t('private_profiles.comment.thumbnail') }}
        </div>
        <div class="text-gray-200 pl-4 sm:pl-0">
          {{ $t('private_profiles.comment.model_title') }}
        </div>
        <div class="hidden sm:block text-gray-200">
          {{ $t('private_profiles.comment.comment') }}
        </div>
      </div>

      <hr class='border-gray-500 my-2'>
    </div>
    <!-- 評論列表 -->
    <div v-if="currentItems.length > 0" 
         class="comments-list flex-1 overflow-auto scrollbar pr-1 -mr-3"
    >
      <profile-review-item 
        v-for="(comment, index) in currentItems" 
        :review="comment" 
      />

      <!-- 在有 items 且持續加載中，顯示 loading -->
      <template v-if="state.loading">
        <div class="flex flex-col justify-center items-center py-8">
          <div class="text-14px text-grey-3">
            <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
          </div>
        </div>
      </template>
      <!-- 在有 items 且加載完全後，顯示 No more data -->
      <template v-else-if="!state.loading && !pageInfo[state.currentRating].hasMore">
        <div class="flex flex-col justify-center items-center py-8">
          <div class="text-14px text-grey-3">
            {{ $t('home_post_collection.no_more_data')  }}
          </div>
        </div>
      </template>
    </div>

    <div v-else-if="state.loading || state.initialLoading" class="flex-1 flex flex-col items-center justify-center mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
      <p class="text-16px font-medium text-center">
        <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
      </p>
    </div>

    <!-- 空狀態 -->
    <div v-else-if="currentItems.length === 0 && !state.loading"
         class="flex-1 flex flex-col items-center justify-center mt-[15%] lg:mt-[20%] 2xl:mt-[15%]"
    >
      <img src="../../images/comments.svg" class="block w-8" alt="No comments" />
      <p class="text-16px font-medium text-center">
        {{ $t('private_profiles.comment.no_comment_fount') }}
      </p>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted, onUnmounted, computed } from 'vue';
import { debounce } from 'lodash';

export default defineComponent({
  name: 'ProfileComments',
  setup() {
    const scrollListener = ref(null);
    let aspectContainer = null;
    const originalClassList = new Set();


    const state = reactive({
      loading: false,
      initialLoading: true,
      itemsCount: 20,
      currentRating: 'all',
      currentSearch: '',
    });

    const stateData = reactive({
      all: { items: [], totalCount: 0 },
      '1': { items: [], totalCount: 0 },
      '2': { items: [], totalCount: 0 },
      '3': { items: [], totalCount: 0 },
      '4': { items: [], totalCount: 0 },
      '5': { items: [], totalCount: 0 },
    });

    const pageInfo = reactive({
      all: { page: 1, hasMore: true },
      '1': { page: 1, hasMore: true },
      '2': { page: 1, hasMore: true },
      '3': { page: 1, hasMore: true },
      '4': { page: 1, hasMore: true },
      '5': { page: 1, hasMore: true },
    });

    const ratingCounts = reactive({
      all: 0,
      '1': 0,
      '2': 0,
      '3': 0,
      '4': 0,
      '5': 0,
    });

    const commentLinks = [
        {
          key: 'comments',
          titleKey: 'private_profiles.comment.comments',
          path: '/profile/comments'
        },
        {
          key: 'reviews',
          titleKey: 'private_profiles.comment.reviews_&_ratings',
          path: '/profile/reviews'
        }
      ]

    const ratingOptions = [
      { value: 'all', label: 'All' },
      { value: '5', label: '5 ★' },
      { value: '4', label: '4 ★' },
      { value: '3', label: '3 ★' },
      { value: '2', label: '2 ★' },
      { value: '1', label: '1 ★' }
    ];

    const currentItems = computed(() => stateData[state.currentRating].items);

    const fetchComments = async (page, perPage) => {
      if (!pageInfo[state.currentRating].hasMore || state.loading) return;
      
      state.loading = true;
      
      const url = `/api/v1/profile/comments`;
      
      const params = new URLSearchParams({
        page: page,
        per_page: perPage,
        kw: state.currentSearch,
        type: 'reviews',
        stars: state.currentRating,
        get_counts_only: 'false'
      });

      try {
        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();
          
          stateData[state.currentRating].totalCount = data.pagination.total_count;

          categorizeItems(data.data, state.currentRating);

          pageInfo[state.currentRating].page++;
          pageInfo[state.currentRating].hasMore = pageInfo[state.currentRating].page < data.pagination.total_pages;

        }
      } catch (error) {
        console.error('Error fetching comments:', error);
        pageInfo[rating].hasMore = false;
      } finally {
        state.loading = false;
        state.initialLoading = false;
      }
    };

    const fetchRatingCounts = async () => {
      try {
        const url = `/api/v1/profile/comments`;
        const params = new URLSearchParams({
          type: 'reviews',
          kw: state.currentSearch,
          get_counts_only: 'true'
        });

        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();
          if (data.rating_counts) {
            Object.assign(ratingCounts, data.rating_counts);
          }
        }
      } catch (error) {
        console.error('Error fetching rating counts:', error);
      }
    };

    const categorizeItems = (newItems, targetRating) => {
      newItems.forEach(item => {
        if (!stateData.all.items.some(existingItem => existingItem.id === item.id)) {
          stateData.all.items.push(item);
        }

        const itemRating = item.stars.toString();
        if (targetRating === 'all' || targetRating === itemRating) {
          if (!stateData[itemRating].items.some(existingItem => existingItem.id === item.id)) {
            stateData[itemRating].items.push(item);
          }
        }
        
        if (targetRating !== 'all' && !stateData[targetRating].items.some(existingItem => existingItem.id === item.id)) {
          stateData[targetRating].items.push(item);
        }
      });
    };

    const resetAllStates = () => {
      Object.keys(stateData).forEach(key => {
        stateData[key].items = [];
        stateData[key].totalCount = 0;
        pageInfo[key].page = 1;
        pageInfo[key].hasMore = true;
      });
    };

    const handleSearch = async () => {
      resetAllStates();
      await fetchComments(1, state.itemsCount);
      await fetchRatingCounts();
    };

    const handleRatingFilter = async (rating) => {
      if (state.currentRating === rating) return;

      state.currentRating = rating;
      
      if (stateData[rating].items.length < state.itemsCount && pageInfo[rating].hasMore) {
        await fetchComments(pageInfo[rating].page, state.itemsCount);
      } else {
        await fetchComments(1, state.itemsCount);
      }
    };

    const handleClear = async () => {
      state.currentSearch = '';
      await handleSearch();
    };

    const handleScroll = debounce(() => {
      if (!pageInfo.hasMore) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrolledPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
      
      if (scrolledPercentage > 70 && !state.loading) {
        fetchComments(pageInfo.page, state.itemsCount);
      }
    }, 100);

    const addScrollListener = () => {
      removeScrollListener();
      scrollListener.value = handleScroll;
      window.addEventListener('scroll', scrollListener.value, { passive: true });
    };

    const removeScrollListener = () => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value);
        scrollListener.value = null;
      }
    };

    const getNavLinkClass = (tabKey) => {
      return tabKey === 'reviews' 
        ? 'border-primary font-medium flex-1 text-center px-4'
        : 'border-transparent flex-1 text-center px-4';
    };

    const getRatingTabClass = (ratingValue) => {
      return state.currentRating === ratingValue
        ? 'active bg-gray-400'
        : 'hover:bg-gray-400';
    };

    const removeAspectRatio = () => {
      const reviewsList = document.querySelector('#reviews-list');
      const aspectContainer = reviewsList?.closest('[class*="aspect-["]');
        
      aspectContainer?.classList.forEach(cls => {
        if (cls.startsWith('aspect-[')) {
          aspectContainer.classList.remove(cls);
        }
      });
    };

    // 生命周期
    onMounted(async () => {
      await fetchRatingCounts();
      await fetchComments(1, state.itemsCount);
      addScrollListener();
      removeAspectRatio();
    });

    onUnmounted(() => {
      removeScrollListener();
    });

    return {
      state,
      stateData,
      pageInfo,
      currentItems,
      commentLinks,
      ratingOptions,
      ratingCounts,
      getNavLinkClass,
      getRatingTabClass,
      handleSearch,
      handleClear,
      handleRatingFilter
    };
  }
});
</script>